import { Autocomplete, Checkbox, Divider, Grid, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react'
import contactStyle from "../../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import styles from "../../../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import globalStyles from "../../../../../assets/styles/global.module.scss";
import { useShallow } from 'zustand/react/shallow';
import { currentActiveChatbotThreadTypeNew, manualAgentData } from '../../../../../models/axiosModel';
import { UserContext, UserContextPayload } from '../../../../../context/userContext';
import { getUserFullName } from '../../../../../utils/constants';
import Required from '../../../../../components/utility/required';
import GlobalButton from '../../../../../components/utility/globalButton/button';
import { getAgentName, isLoggedInUserAgent } from '../../../utils';
import { useRuleBasedInboxStore } from '../../../../../store/ruleBasedInboxStore';
import { renderAgentOption } from '../../../renderUtils';

interface RemoveAgentPopupProps {
    handleToggleManualOverride: (user_assignment_id: string, flag_delete_agent: boolean, flag_send_agent_activity_message: boolean) => void,
    flagSendAgentActivityMsg: boolean
}

const RemoveAgentPopup: React.FC<RemoveAgentPopupProps> = (props) => {
    
    const { handleToggleManualOverride, flagSendAgentActivityMsg } = props;

    const { userDetail } = useContext(UserContext) as UserContextPayload; 

    const { currentActiveChatbotThread, setFlagRemoveAgentPopup } = useRuleBasedInboxStore(useShallow((state) => ({
        currentActiveChatbotThread: state.currentActiveChatbotThread as currentActiveChatbotThreadTypeNew,
        setFlagRemoveAgentPopup: state.setFlagRemoveAgentPopup,
    })))

    const shouldShowRemoveAgentDesc = () => {
        if(currentActiveChatbotThread && currentActiveChatbotThread?.manual_agent_data?.length === 1){
            return true;
        }

        return false;
    }

    const [manualAgentData, setManualAgentData] = useState(currentActiveChatbotThread?.manual_agent_data ?? [])
    const [selectedAgent, setSelectedAgent] = useState<manualAgentData | null>(shouldShowRemoveAgentDesc() ? currentActiveChatbotThread?.manual_agent_data?.[0] : null);
    const [flagSendMsg, setFlagSendMsg] = useState<boolean>(flagSendAgentActivityMsg);

    const handleAgentAction = () => {
        if(selectedAgent){
            handleToggleManualOverride(String(selectedAgent.user_assignment_id), true, flagSendMsg);
            setFlagRemoveAgentPopup(false);
        }
    }

    const getRemoveAgentDesc = () => {
        return `Are you sure you want to remove ${isLoggedInUserAgent (manualAgentData?.[0]?.user_assignment_id, userDetail?.user_assignment_id) ? 'yourself as the agent' : `${getAgentName(manualAgentData?.[0] as manualAgentData)}`} from this conversation?`;
    }

    const onChangeAgent = (value: manualAgentData | null) => {
        setSelectedAgent(value);
    } 

    useEffect(() => {
        if(currentActiveChatbotThread && currentActiveChatbotThread?.manual_agent_data){
            if(currentActiveChatbotThread?.manual_agent_data?.length === 0){
                setFlagRemoveAgentPopup(false); // Close the popup if no agent is assigned.
            }
            setSelectedAgent(shouldShowRemoveAgentDesc() ? currentActiveChatbotThread?.manual_agent_data?.[0] : null);
            setManualAgentData(currentActiveChatbotThread?.manual_agent_data)
        }
    }, [currentActiveChatbotThread])

    return (
        <>
            <Grid item xs={12}>
                <Grid container direction={"column"} justifyContent={"center"}>
                    <Grid item textAlign={"center"}>
                        <Typography className={contactStyle.popupTitleTypography}>
                            Remove Agent
                        </Typography>
                    </Grid>

                    <Divider className={contactStyle.popupTitleDivider} /> 

                    <Grid item>
                        <Grid container className={contactStyle.popupBodyGridContainer} gap={1}>
                            {shouldShowRemoveAgentDesc() ? 
                                <Grid item xs={12}>
                                    <Typography className={`${globalStyles.secondaryFontSize} ${globalStyles.secondaryFontWeight}`}>
                                        {getRemoveAgentDesc()}
                                    </Typography>
                                </Grid>
                                :
                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={contactStyle.popupFieldTypography}> Agent <Required /> </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete 
                                                className={`${contactStyle.contactFilterDropDowns} ${contactStyle.contactFilterDropdownsInPopup}`}
                                                options={manualAgentData ?? []} 
                                                renderOption={(props, option) => renderAgentOption(props, option, option.thread_manual_agent_id)}
                                                getOptionLabel={(option) => getUserFullName(option)} 
                                                value={selectedAgent as manualAgentData | null}
                                                onChange={(e, newValue) => onChangeAgent(newValue)}
                                                renderInput={(params) => <TextField {...params} placeholder="Select Agent" />}
                                                ListboxProps={{
                                                    className: styles.autocompleteListbox,
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            }

                            {flagSendAgentActivityMsg && 
                                <Grid item className={globalStyles.deleteConfirmPopupSubTextStyle} xs={12}>
                                    <Grid container className={globalStyles.displayFlex}>
                                        <Grid item>
                                            <Checkbox
                                                className={styles.sendAgentActivityConfirmationCheckbox} 
                                                disableRipple 
                                                checked={flagSendMsg}
                                                onChange={(event) => { setFlagSendMsg(event.target.checked) }} 
                                            />
                                        </Grid>
                                        <Grid item className={`${globalStyles.primaryFontSize} ${globalStyles.secondaryFontSize} ${globalStyles.displayFlex}`}>
                                            Send Agent Activity Message
                                        </Grid>
                                    </Grid>
                                </Grid>
                            }
                        </Grid>
                    </Grid>

                    <Grid item>
                        <Grid container justifyContent={"center"} alignItems={"center"} gap={"12px"} className={styles.assignAgentContainer}>
                            <Grid item>
                                <GlobalButton
                                    buttonText="Cancel"
                                    sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                    className='borderedButtonStyle'
                                    onClick={() => { setSelectedAgent(null); setFlagRemoveAgentPopup(false); }}
                                >
                                </GlobalButton>
                            </Grid>
                            <Grid item>
                                <GlobalButton
                                    buttonText="Remove"
                                    sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                    className='primaryButtonStyle'
                                    disabled={selectedAgent == null}
                                    onClick={() => handleAgentAction()}
                                >
                                </GlobalButton>
                            </Grid>
                        </Grid>
                    </Grid>

                </Grid>
            </Grid>
        </>
    )
}

export default RemoveAgentPopup
